import { useCallback, useMemo } from 'react';
import {
  collectScopedVariables,
  resolveVariableExpanded,
  nextVariableColor,
  generateVariableId,
} from '../../../../utils/variableScope';
import { buildVariableOfType } from '../../../../utils/variableTypeChange';

/**
 * Doc-scoped variables for the library master view. No campaign context —
 * pills resolve from these only; campaign refs show unresolved (template).
 */
export function useDocVariables({
  docVariables,
  setDocVariables,
  setVariablesColumnWidths,
  saveNow,
}) {
  const scopedVariables = useMemo(
    () => collectScopedVariables({ docVariables }),
    [docVariables]
  );

  const resolveVariable = useCallback(
    (name) => resolveVariableExpanded(scopedVariables, name),
    [scopedVariables]
  );

  const handleDocVariablesChange = useCallback(
    (vars) => {
      setDocVariables(vars);
      // Persist now — panel already idle-debounces keystrokes; delaying again
      // loses variables on hard refresh.
      saveNow({ variables: vars });
    },
    [setDocVariables, saveNow]
  );

  const handleVariablesColumnWidthsChange = useCallback(
    (widths) => {
      setVariablesColumnWidths(widths);
      saveNow({ variablesColumnWidths: widths });
    },
    [setVariablesColumnWidths, saveNow]
  );

  const handleCreateVariable = useCallback(
    (name, type) => {
      setDocVariables((prev) => {
        const next = [
          ...prev,
          buildVariableOfType(
            { id: generateVariableId(), name, color: nextVariableColor(prev) },
            type
          ),
        ];
        saveNow({ variables: next });
        return next;
      });
    },
    [setDocVariables, saveNow]
  );

  return {
    scopedVariables,
    resolveVariable,
    handleDocVariablesChange,
    handleVariablesColumnWidthsChange,
    handleCreateVariable,
  };
}
